import { useState, useEffect } from "react";
import { api } from "../App";
import { toast } from "sonner";
import { CheckCircle, AlertCircle, XCircle, Clock } from "lucide-react";

const STATUS_OPTIONS = [
  { key: "on_track", label: "On Track", icon: CheckCircle, color: "var(--g)" },
  { key: "blocked", label: "Blocked", icon: AlertCircle, color: "#F59E0B" },
  { key: "done", label: "Done", icon: CheckCircle, color: "var(--c)" },
];

export default function EODUpdateOverlay({ onComplete }) {
  const [data, setData] = useState(null);
  const [updates, setUpdates] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const r = await api.get("/me/daily-update-status");
      setData(r.data);
      // Pre-fill already-updated items as "on_track"
      const pre = {};
      (r.data.tasks || []).forEach(t => { if (t.updated_today) pre[`task:${t.task_id}`] = "on_track"; });
      (r.data.briefs || []).forEach(b => { if (b.updated_today) pre[`brief:${b.brief_id}`] = "on_track"; });
      setUpdates(pre);
    } catch { toast.error("Failed to load update status"); }
    finally { setLoading(false); }
  };

  const setItemStatus = (key, status) => {
    setUpdates(prev => ({ ...prev, [key]: status }));
  };

  const totalItems = (data?.tasks?.length || 0) + (data?.briefs?.length || 0);
  const taggedItems = Object.keys(updates).length;
  const pct = totalItems > 0 ? Math.round((taggedItems / totalItems) * 100) : 100;
  const canSubmit = pct >= 80;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = Object.entries(updates).map(([key, status_tag]) => {
        const [type, id] = key.split(":");
        return { entity_type: type, entity_id: id, status_tag };
      });
      await api.post("/me/daily-update-status", { updates: payload });
      toast.success("Daily update submitted!");
      onComplete();
    } catch { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, zIndex: 998, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.7)" }}>
      <p style={{ color: "var(--t2)", fontFamily: "var(--font-mono)" }}>Loading...</p>
    </div>
  );

  if (!data || !data.needs_eod_update) { onComplete(); return null; }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 998, background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)", overflowY: "auto", padding: "40px 20px" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", background: "var(--s1)", borderRadius: 16, border: "1px solid var(--bdr)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--bdr)", background: "var(--s2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Clock size={20} style={{ color: "var(--r)" }} />
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--t1)", fontFamily: "var(--font-orb)" }}>Daily Update Required</h2>
              <p style={{ fontSize: 12, color: "var(--t3)", marginTop: 2 }}>Tag each item before you wrap up for the day</p>
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 6, background: "var(--s3)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: canSubmit ? "var(--g)" : "var(--a)", borderRadius: 3, transition: "width .3s" }} />
            </div>
            <span style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: canSubmit ? "var(--g)" : "var(--t2)", fontWeight: 600 }}>{pct}%</span>
          </div>
        </div>

        {/* Tasks */}
        {(data.tasks || []).length > 0 && (
          <div style={{ padding: "12px 24px" }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", color: "var(--t3)", fontFamily: "var(--font-mono)", marginBottom: 8 }}>TASKS ({data.tasks.length})</p>
            {data.tasks.map(t => {
              const key = `task:${t.task_id}`;
              const current = updates[key];
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--bdr)" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: "var(--t1)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</p>
                    <p style={{ fontSize: 11, color: "var(--t3)" }}>{t.status}{t.priority ? ` · ${t.priority}` : ""}</p>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {STATUS_OPTIONS.map(opt => (
                      <button key={opt.key} onClick={() => setItemStatus(key, opt.key)}
                        style={{
                          padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, border: "none", cursor: "pointer",
                          background: current === opt.key ? opt.color + "18" : "var(--s2)",
                          color: current === opt.key ? opt.color : "var(--t3)",
                          transition: "all .15s"
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Briefs */}
        {(data.briefs || []).length > 0 && (
          <div style={{ padding: "12px 24px" }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", color: "var(--t3)", fontFamily: "var(--font-mono)", marginBottom: 8 }}>BRIEFS ({data.briefs.length})</p>
            {data.briefs.map(b => {
              const key = `brief:${b.brief_id}`;
              const current = updates[key];
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--bdr)" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: "var(--t1)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</p>
                    <p style={{ fontSize: 11, color: "var(--t3)" }}>{b.company} · {b.status}</p>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {STATUS_OPTIONS.map(opt => (
                      <button key={opt.key} onClick={() => setItemStatus(key, opt.key)}
                        style={{
                          padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, border: "none", cursor: "pointer",
                          background: current === opt.key ? opt.color + "18" : "var(--s2)",
                          color: current === opt.key ? opt.color : "var(--t3)",
                          transition: "all .15s"
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--bdr)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--t3)" }}>{taggedItems}/{totalItems} items tagged · need {Math.ceil(totalItems * 0.8)} to submit</span>
          <button onClick={handleSubmit} disabled={!canSubmit || submitting}
            style={{
              padding: "10px 28px", borderRadius: 10, border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
              background: canSubmit ? "var(--r)" : "var(--s3)", color: canSubmit ? "#fff" : "var(--t3)",
              fontSize: 14, fontWeight: 700, transition: "all .15s"
            }}>
            {submitting ? "Submitting..." : "Submit Daily Update"}
          </button>
        </div>
      </div>
    </div>
  );
}
